import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, User, Phone, MessageSquare, CalendarDays, Trash2, CheckSquare, XSquare, ChevronDown } from 'lucide-react';
import { Lead, LeadState } from '../../types/lead';
import { StateBadge, IcpBadge } from '../ui/Badge';
import { useDataStore } from '../../context/DataStoreContext';
import { BookingModal } from '../booking/BookingModal';
interface LeadsTableProps {
  leads: Lead[];
}
export function LeadsTable({
  leads
}: LeadsTableProps) {
  const navigate = useNavigate();
  const {
    updateLead,
    deleteLead
  } = useDataStore();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bookingLeadId, setBookingLeadId] = useState<string | null>(null);
  const allSelected = leads.length > 0 && selectedIds.size === leads.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < leads.length;
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.id)));
    }
  };
  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };
  const handleBulkVerify = (verified: boolean) => {
    selectedIds.forEach(id => updateLead(id, {
      VERIFICADO: verified
    }));
    setSelectedIds(new Set());
  };
  const handleBulkState = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value as LeadState;
    if (!newState) return;
    selectedIds.forEach(id => updateLead(id, {
      STATE: newState
    }));
    setSelectedIds(new Set());
  };
  const handleBulkDelete = () => {
    if (window.confirm(`¿Estás seguro de eliminar ${selectedIds.size} leads? Esta acción no se puede deshacer.`)) {
      selectedIds.forEach(id => deleteLead(id));
      setSelectedIds(new Set());
    }
  };
  const maskPhone = (phone: string) => {
    if (!phone) return '';
    const prefix = phone.substring(0, 3);
    const suffix = phone.substring(phone.length - 2);
    return `${prefix} *** ** ${suffix}`;
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short'
    });
  };
  const bookingLead = leads.find(l => l.id === bookingLeadId);
  return <div className="card overflow-hidden" data-id="element-481">
      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && <div className="bg-growthly-amber/10 border-b border-growthly-amber/20 px-4 py-3 flex items-center justify-between" data-id="element-482">
          <div className="flex items-center space-x-4" data-id="element-483">
            <span className="text-[13px] font-medium text-growthly-amber" data-id="element-484">
              {selectedIds.size} seleccionados
            </span>
            <div className="h-4 w-px bg-growthly-amber/20" data-id="element-485"></div>
            <button onClick={() => handleBulkVerify(true)} className="flex items-center text-[12px] font-medium text-growthly-text hover:text-white transition-colors" data-id="element-486">
              <CheckSquare className="w-4 h-4 mr-1.5 text-growthly-green" data-id="element-487" />
              Verificar
            </button>
            <button onClick={() => handleBulkVerify(false)} className="flex items-center text-[12px] font-medium text-growthly-text hover:text-white transition-colors" data-id="element-488">
              <XSquare className="w-4 h-4 mr-1.5 text-growthly-muted" data-id="element-489" />
              Quitar verificación
            </button>
            <div className="relative" data-id="element-490">
              <select onChange={handleBulkState} value="" className="appearance-none bg-transparent border-none text-[12px] font-medium text-growthly-text hover:text-white cursor-pointer focus:ring-0 pr-6" data-id="element-491">
                <option value="" disabled data-id="element-492">
                  Cambiar estado...
                </option>
                <option value="Nuevo" data-id="element-493">Nuevo</option>
                <option value="Contactado" data-id="element-494">Contactado</option>
                <option value="Conversación" data-id="element-495">Conversación</option>
                <option value="Micro-acuerdo" data-id="element-496">Micro-acuerdo</option>
                <option value="EnviadoPropuesta" data-id="element-497">EnviadoPropuesta</option>
                <option value="LeadCualificado" data-id="element-498">LeadCualificado</option>
                <option value="Rechazado" data-id="element-499">Rechazado</option>
              </select>
              <ChevronDown className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 text-growthly-muted pointer-events-none" data-id="element-500" />
            </div>
          </div>
          <button onClick={handleBulkDelete} className="flex items-center text-[12px] font-medium text-growthly-red hover:text-red-400 transition-colors" data-id="element-501">
            <Trash2 className="w-4 h-4 mr-1.5" data-id="element-502" />
            Eliminar
          </button>
        </div>}

      <div className="overflow-x-auto" data-id="element-503">
        <table className="min-w-full divide-y divide-white/5" data-id="element-504">
          <thead className="bg-growthly-panel" data-id="element-505">
            <tr data-id="element-506">
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider w-10" data-id="element-507">
                <input type="checkbox" checked={allSelected} ref={input => {
                if (input) input.indeterminate = someSelected;
              }} onChange={toggleSelectAll} className="rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-508" />
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-509">
                Negocio
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-510">
                Contacto
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-511">
                ICP / Score
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-512">
                Estado
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-513">
                Próx. Acción
              </th>
              <th scope="col" className="px-4 py-3 text-left text-[11px] font-medium text-growthly-muted uppercase tracking-wider" data-id="element-514">
                Evidencia
              </th>
            </tr>
          </thead>
          <tbody className="bg-growthly-surface divide-y divide-white/5" data-id="element-515">
            {leads.map(lead => {
            const isSelected = selectedIds.has(lead.id);
            return <tr key={lead.id} className={`h-[72px] transition-colors cursor-pointer group ${isSelected ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'}`} onClick={() => navigate(`/app/leads/${lead.id}`)} data-id="element-516">
                  <td className="px-4 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-517">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(lead.id)} className="rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-518" />
                  </td>
                  <td className="px-4" data-id="element-519">
                    <div className="flex flex-col justify-center h-full" data-id="element-520">
                      <span className="text-[14px] font-semibold text-growthly-text group-hover:underline" data-id="element-521">
                        {lead.NOMBRE_NEGOCIO}
                      </span>
                      <span className="text-[12px] text-growthly-muted" data-id="element-522">
                        {lead.DIRECCION.ciudad || 'Sin ciudad'} •{' '}
                        {lead.LEAD_SOURCE}
                      </span>
                    </div>
                  </td>
                  <td className="px-4" data-id="element-523">
                    <div className="flex items-center justify-between" data-id="element-524">
                      <div className="flex flex-col" data-id="element-525">
                        <span className="text-[13px] text-growthly-text" data-id="element-526">
                          {lead.DECISOR_NOMBRE || <span className="italic text-growthly-muted" data-id="element-527">
                              Sin nombre
                            </span>}
                        </span>
                        <div className="flex items-center text-[12px] text-growthly-muted mt-0.5" data-id="element-528">
                          <span data-id="element-529">
                            {maskPhone(lead.TELEFONO_WHATSAPP) || 'Sin teléfono'}
                          </span>
                          {lead.VERIFICADO ? <CheckCircle2 className="w-3 h-3 text-growthly-green ml-1" data-id="element-530" /> : <AlertCircle className="w-3 h-3 text-growthly-red ml-1" data-id="element-531" />}
                        </div>
                      </div>
                      {/* Inline actions on hover */}
                      <div className="hidden group-hover:flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()} data-id="element-532">
                        <a href={lead.TELEFONO_WHATSAPP ? `tel:${lead.TELEFONO_WHATSAPP}` : '#'} className="p-1.5 text-growthly-muted hover:text-growthly-text hover:bg-white/10 rounded" title="Llamar" data-id="element-533">
                          <Phone className="w-4 h-4" data-id="element-534" />
                        </a>
                        <a href={lead.TELEFONO_WHATSAPP ? `https://wa.me/${lead.TELEFONO_WHATSAPP.replace(/\D/g, '')}` : '#'} target="_blank" rel="noopener noreferrer" className="p-1.5 text-growthly-muted hover:text-growthly-text hover:bg-white/10 rounded" title="WhatsApp" data-id="element-535">
                          <MessageSquare className="w-4 h-4" data-id="element-536" />
                        </a>
                        <button onClick={() => setBookingLeadId(lead.id)} className="p-1.5 text-growthly-muted hover:text-growthly-text hover:bg-white/10 rounded" title="Agendar" data-id="element-537">
                          <CalendarDays className="w-4 h-4" data-id="element-538" />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-539">
                    <div className="flex items-center space-x-3" data-id="element-540">
                      <IcpBadge priority={lead.ICP_PRIORITY} data-id="element-541" />
                      <div className="flex flex-col w-16" data-id="element-542">
                        <span className="text-[13px] font-medium text-growthly-text" data-id="element-543">
                          {lead.LEAD_SCORE}
                        </span>
                        <div className="w-full h-1.5 bg-white/10 rounded-full mt-1" data-id="element-544">
                          <div className="h-1.5 bg-growthly-muted rounded-full" style={{
                        width: `${Math.min(lead.LEAD_SCORE / 100 * 100, 100)}%`
                      }} data-id="element-545"></div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-546">
                    <div className="flex flex-col items-start" data-id="element-547">
                      <StateBadge state={lead.STATE} data-id="element-548" />
                      {lead.MICRO_ACCION_TYPE && <span className="text-[11px] text-growthly-muted mt-1" data-id="element-549">
                          {lead.MICRO_ACCION_TYPE}
                        </span>}
                    </div>
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-550">
                    <div className="flex items-center space-x-2" data-id="element-551">
                      <div className="w-6 h-6 rounded-full bg-growthly-panel flex items-center justify-center text-growthly-muted" data-id="element-552">
                        <User className="w-3 h-3" data-id="element-553" />
                      </div>
                      <span className="text-[13px] text-growthly-text" data-id="element-554">
                        {formatDate(lead.NEXT_ACTION_DATE)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 whitespace-nowrap" data-id="element-555">
                    <div className="flex items-center space-x-2" data-id="element-556">
                      <span className="text-[13px] font-medium text-growthly-text w-8" data-id="element-557">
                        {lead.EVIDENCE_COMPLETENESS}%
                      </span>
                      <div className="w-16 h-2 bg-white/10 rounded-full" data-id="element-558">
                        <div className={`h-2 rounded-full ${lead.EVIDENCE_COMPLETENESS === 100 ? 'bg-growthly-green' : 'bg-growthly-text'}`} style={{
                      width: `${lead.EVIDENCE_COMPLETENESS}%`
                    }} data-id="element-559"></div>
                      </div>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-white/5 flex items-center justify-between sm:px-6 bg-growthly-panel" data-id="element-560">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-561">
          <div data-id="element-562">
            <p className="text-[13px] text-growthly-muted" data-id="element-563">
              Mostrando{' '}
              <span className="font-medium text-growthly-text" data-id="element-564">1</span> a{' '}
              <span className="font-medium text-growthly-text" data-id="element-565">
                {leads.length}
              </span>{' '}
              de{' '}
              <span className="font-medium text-growthly-text" data-id="element-566">
                {leads.length}
              </span>{' '}
              resultados
            </p>
          </div>
          <div data-id="element-567">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-568">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-white/10 bg-growthly-surface text-[13px] font-medium text-growthly-muted hover:bg-white/5" data-id="element-569">
                Anterior
              </button>
              <button className="relative inline-flex items-center px-4 py-2 border border-white/10 bg-white/10 text-[13px] font-medium text-growthly-text" data-id="element-570">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-white/10 bg-growthly-surface text-[13px] font-medium text-growthly-muted hover:bg-white/5" data-id="element-571">
                Siguiente
              </button>
            </nav>
          </div>
        </div>
      </div>

      <BookingModal isOpen={!!bookingLeadId} onClose={() => setBookingLeadId(null)} leadId={bookingLead?.id} leadName={bookingLead?.NOMBRE_NEGOCIO} data-id="element-572" />
    </div>;
}